import styled from 'styled-components';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { removeOrganizationMember } from 'api/OrganizationAPI';
import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import { Modal, ModalBody, ModalHeader, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { spacing, colors, Body2, BoldText } from 'ui/theme';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import {
  MODAL_NAMES,
  MembersPayload,
  useOrganizationModalsContext,
} from '../../contexts/OrganizationModalsContext';
import { useSelectionContext } from '../../contexts/SelectionContext';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import { fullName } from '../../utils';

const Body2Light = styled(Body2)`
  color: ${colors.textSecondary};
`;

const SpanLight = styled.span`
  color: ${colors.textSecondary};
`;

const BorderedBox = styled.div`
  border: ${spacing.single} solid ${colors.borderPrimary};
  border-radius: ${spacing.quarter};
  background-color: ${colors.backgroundSecondary};
  padding: ${spacing.base};
  margin-top: ${spacing.base};
`;

const userIdsStr = (userIds: string[]) =>
  userIds.length > 3
    ? `${userIds.slice(0, 3).join(', ')}... and ${userIds.length - 3} more`
    : userIds.join(', ');

const createSuccessMessage = (userIds: string[]) => {
  const num = userIds.length;
  return `Revoked access for ${num} member${num === 1 ? '' : 's'}: ${userIdsStr(userIds)}.`;
};

const createErrorMessage = (userIds: string[]) => {
  const num = userIds.length;
  return `Failed to revoke access for ${num} member${num === 1 ? '' : 's'}: ${userIdsStr(
    userIds,
  )}.`;
};

export const BulkRevokeAccessModal = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { deselectByIds } = useSelectionContext();
  const [results, setResults] = useState<PromiseSettledResult<AxiosResponse<any, any>>[]>([]);
  const { refetchAll } = useOrganizationMembersQueryContext();
  const { hideModal, modalState } = useOrganizationModalsContext();

  const members = (modalState?.payload as MembersPayload)?.members || [];

  useEffect(() => {
    if (results.length) {
      /**
       * Get successful removals, and toast with success message.
       */
      const successes = results.filter((result) => result.status === 'fulfilled');
      const successIds = successes
        .filter(
          (result) => (result as PromiseFulfilledResult<AxiosResponse>)?.value?.status === 204,
        )
        .map(
          (result) =>
            (result as PromiseFulfilledResult<AxiosResponse>)?.value?.config?.params?.userId,
        );

      if (successIds.length) {
        const message = createSuccessMessage(successIds);
        dispatch(addPositiveToast(message));
        /**
         * Deselect the removed members -- otherwise the bulk checkbox will remain checked with
         * ghost members selected.
         */
        deselectByIds(successIds);
      }

      /**
       * Get failed removals, and toast with error message.
       */
      const failures = results.filter((result) => result.status === 'rejected');
      const failureIds = failures
        .map((result) => (result as PromiseRejectedResult)?.reason?.config?.params?.userId)
        .filter(Boolean);

      if (failureIds.length) {
        const message = createErrorMessage(failureIds);
        dispatch(
          addNegativeToastWithErrorId(
            message,
            'bulkRevokeAccess',
            (failures[0] as PromiseRejectedResult).reason,
          ),
        );
      }

      refetchAll();
      hideModal();
      setResults([]);
    }
  }, [results, deselectByIds, dispatch, hideModal, refetchAll]);

  const onSubmit = async () => {
    setIsLoading(true);

    const rs = await Promise.allSettled(members.map((member) => removeOrganizationMember(member)));
    setResults(rs);

    setIsLoading(false);
  };

  return (
    <Modal
      id="bulk-revoke-access-modal"
      isModalOpen={modalState?.name === MODAL_NAMES.REVOKE_ACCESS}
      onClose={hideModal}
      variant={MODAL_VARIANTS.DELETE}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>Revoke access?</ModalHeader>
      <ModalBody>
        <Body2Light>
          Are you sure you want to revoke access for these{' '}
          <BoldText>{members.length} members</BoldText>?
        </Body2Light>
        <Body2Light>
          They’ll no longer be able to login and access any projects in your organization.
        </Body2Light>
        <BorderedBox>
          {members.map((member) => (
            <Body2 key={member.userId}>
              <BoldText>{fullName(member)}</BoldText>&nbsp;
              <SpanLight>({member.email})</SpanLight>
            </Body2>
          ))}
        </BorderedBox>
      </ModalBody>
      <ModalFooter>
        <DestructiveButton
          size={BUTTON_SIZES.LARGE}
          onClick={onSubmit}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Revoke {members.length} members
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
